// 11 §7 — intent authoring state machine. Pure of Phaser/React/Zustand so Vitest covers every
// transition (18.1 `authoring.statemachine`). The AuthoringStore owns one instance per human.
//
//          Tab / click hero (mine)                    Esc
//  Idle ───────────────────────▶ HeroSelected ◀──────────── SlotChosen
//    ▲                               │  1 / 2 / B, or click slot in queue
//    │                               ▼
//    │                           SlotChosen ──── choose action (hotbar) ──▶ ActionChosen
//    │                                                                        │ targets complete
//    │                                                                        ▼
//    │                      (legal() on every pointer move)              Provisional
//    │                                                                        │ submit_intent (optimistic)
//    └──── Lock plan (Space: `ready` commits every hero) ◀─── slots filled or Hold ◀──┘
//
// docs/33 E1: a board click with no action chosen does what it obviously means — `suggest()`
// reads the square under the pointer: one of my heroes selects it, a foe in reach is attacked, a
// foe out of reach is closed on (a Move, then the attack) when two actions are free, a stash is
// opened, a puzzle mechanism is worked (docs/41: pulled, pushed, touched, read — walking over
// first if it must), open ground in reach is walked to; anything else says why not. The pointer only ever
// plans; the only commitment is Lock plan, for all of the player's heroes at once.
import type { ActionArgs, Direction, FixtureView, Intent, InteractVerb, LegalityVerdict, Slot, StandAt, ThingView, Tile } from '../sim/types';
import { tileEq } from '../sim/types';
import { gapTo, originFor, sizeOf, tilesAt } from '../sim/body';
import type { ActionDef } from '../sim/actions';
import { HOLD, MOVE } from '../sim/actions';
import { secretLine, secretName, trapLive } from '../sim/secrets';
import { BOON_WORDS } from '../crawl/crawl';
import { describeThing, thingVerbs } from '../matter/matter';
import { curioLabel, curioNoun, curioShort, curioVerb } from '../snares/snares';

export type { Direction };

export type AuthoringState =
  | { kind: 'Idle' }
  | { kind: 'HeroSelected'; heroId: number }
  | { kind: 'SlotChosen'; heroId: number; slot: Slot }
  | { kind: 'ActionChosen'; heroId: number; slot: Slot; action: ActionDef; pending: Partial<{ tile: Tile; unit: number; dir: Direction; item: number }> }
  | { kind: 'Provisional'; heroId: number; slot: Slot; intent: Intent };

/** `size`: docs/79, a huge body's side (its `tile` is the origin, the top-left square). */
export interface HeroInfo { heroId: number; unitId: number; classId: string; tile: Tile; name: string; size?: number }

export interface AuthoringContext {
  /** Heroes this human controls, in roster order (10 `my_hero_controls`). */
  heroes(): HeroInfo[];
  /** Actions legal for this hero in this slot (11 §7 hotbar; `legal()` probe in the real client). */
  actions(heroId: number, slot: Slot): ActionDef[];
  legal(intent: Intent): LegalityVerdict;
  unitAt(tile: Tile): number | undefined;
  /** A found, unopened stash on the tile (docs/25): what Interact opens. */
  stashAt?(tile: Tile): number | undefined;
  /** docs/126: an untaken pouch a fallen foe left on the tile: a hero walks onto it to take it. */
  pouchAt?(tile: Tile): boolean;
  /** docs/138 XO7: a find (shown, not taken) lies on this square; `pouchAt` is true there too. */
  findAt?(tile: Tile): boolean;
  /** docs/41: a puzzle room's mechanism on the tile (a lever, block, statue, rune, inscription, the bolt). */
  fixtureAt?(tile: Tile): FixtureView | undefined;
  /**
   * docs/140 DT11: a dungeon trap's plate or wire on the square that the party has found and that is
   * still armed: `name` its guise; a rogue's Disarm takes it apart, anyone else stepping on it springs it.
   */
  snareAt?(tile: Tile): { id: number; name: string; wire: boolean } | undefined;
  /** docs/114 MT4: the thing standing on a square (a barrel, a brazier, a boulder), if one still does. */
  thingAt?(tile: Tile): ThingView | undefined;
  /** docs/41: drag rather than push a block (Shift held). */
  dragMode?(): boolean;
  snapshotHash(): string;
  /** `(hero, slot)` already filled (server row or local). */
  filled(heroId: number, slot: Slot): boolean;
  committed(heroId: number): boolean;
  /** docs/33 E1 (smart clicks): which side a unit is on — mine and other heroes are friends. */
  sideOf?(unit: number): 'friend' | 'foe' | undefined;
  unitName?(unit: number): string;
  /** `legal` with `before` planned first (a Move the click would add ahead of the attack). */
  legalAfter?(intent: Intent, before: Intent[]): LegalityVerdict;
  /** The squares this hero can stop on with a Move in `slot`, cheapest first. */
  stops?(heroId: number, slot: Slot): Array<{ tile: Tile; cost: number }>;
  /** docs/149 MP: whether the hero can walk to `tile` over the pulses to come (a route), beyond this pulse's reach. */
  routeTo?(heroId: number, tile: Tile): boolean;
  /** What the ground on the square is ("Stone floor", "Deep water"), when nothing stands or lies on it. */
  groundAt?(tile: Tile): string | undefined;
  /** docs/66: what a unit's marks mean (arriving, captive, raised), for the hover chip. */
  unitNote?(unit: number): string | undefined;
  /**
   * docs/81 SA3: where every unit stands as this hero's `slot` begins — the hero itself where its
   * earlier slots leave it, friends at their planning ghosts. Aims and targets are read from here.
   */
  standsAt?(heroId: number, slot: Slot): StandAt[];
  /**
   * docs/63: a square the party has never seen (behind a closed door). A chosen action may aim there
   * (a teleport into the dark), but nothing about it is said: no suggestion, no reason it fails.
   */
  unknown?(tile: Tile): boolean;
}

/** docs/33 E1: what a click on a square would do, for the cursor chip, the preview and the click itself. */
export type Suggestion =
  | { kind: 'select'; heroId: number; label: string }
  | { kind: 'plan'; intents: Intent[]; label: string; short?: string; about?: string; tone: 'move' | 'attack' | 'support'; walkTo?: Tile; target?: number; hit?: { damage: number; hpStart: number; hpEnd: number; drops: boolean } }
  | { kind: 'blocked'; label: string; short?: string; target?: number };

export type AuthoringEvent =
  | { type: 'selectHero'; heroId?: number }
  | { type: 'cycleHero'; dir: 1 | -1 }
  | { type: 'chooseSlot'; slot: Slot }
  | { type: 'chooseAction'; action: ActionDef }
  | { type: 'hover'; tile: Tile }            // pointer move → legal probe
  | { type: 'moveCursor'; dx: number; dy: number }
  | { type: 'confirm'; tile?: Tile }         // Enter: confirm the cursor as target
  | { type: 'click'; tile: Tile }            // pointer click on a square (docs/33 E1: never commits)
  | { type: 'pickUnit'; unit: number }
  | { type: 'pickDirection'; dir: Direction }
  | { type: 'escape' }
  | { type: 'hold' }                        // explicit Hold into the chosen slot
  | { type: 'planBonus'; heroId: number; action: ActionDef }   // docs/83 PO8: Change Gear from the inventory
  | { type: 'useItem'; item: number; friend: boolean }   // docs/40: Use Item from the inventory (`friend`: it may be aimed at one)
  | { type: 'clear'; heroId: number; slot: Slot }
  | { type: 'snapshotChanged'; hash: string };

export type AuthoringOutput =
  | { type: 'place'; intent: Intent }
  | { type: 'clear'; heroId: number; slot: Slot }
  | { type: 'reset' }                                    // snapshot changed: drop local + rejected
  | { type: 'route'; heroId: number; tile: Tile }        // docs/149 MP: walk there over several pulses
  | { type: 'announce'; text: string };                  // §14 live region

/** `about`: what is on the probed square, so a refusal still names it. */
export interface Probe { tile: Tile; verdict: LegalityVerdict; about?: string }

export class AuthoringMachine {
  state: AuthoringState = { kind: 'Idle' };
  cursor: Tile = { x: 0, y: 0 };
  probe: Probe | undefined;
  /** docs/33 E1: what a click at the cursor would do, while no action is chosen. */
  suggestion: Suggestion | undefined;
  private suggestKey = '';
  private revision = 1;
  private listeners = new Set<(out: AuthoringOutput) => void>();

  constructor(private readonly ctx: AuthoringContext, private readonly bounds: { w: number; h: number }) {}

  onOutput(cb: (out: AuthoringOutput) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
  private emit(out: AuthoringOutput): void { for (const l of this.listeners) l(out); }

  get selectedHero(): number | undefined { return this.state.kind === 'Idle' ? undefined : this.state.heroId; }
  get slot(): Slot | undefined { return this.state.kind === 'SlotChosen' || this.state.kind === 'ActionChosen' || this.state.kind === 'Provisional' ? this.state.slot : undefined; }

  send(ev: AuthoringEvent): AuthoringState {
    const s = this.state;
    switch (ev.type) {
      case 'snapshotChanged': {
        this.state = { kind: 'Idle' };
        this.probe = undefined;
        this.emit({ type: 'reset' });
        break;
      }
      case 'selectHero': {
        if (ev.heroId === undefined) { this.state = { kind: 'Idle' }; break; }
        const hero = this.ctx.heroes().find((h) => h.heroId === ev.heroId);
        if (!hero) break;                                   // only heroes I control
        this.state = { kind: 'HeroSelected', heroId: hero.heroId };
        this.cursor = { ...hero.tile };
        this.probe = undefined;
        this.emit({ type: 'announce', text: `${hero.name} selected` });
        break;
      }
      case 'cycleHero': {
        const heroes = this.ctx.heroes();
        if (heroes.length === 0) break;
        const cur = this.selectedHero;
        const idx = heroes.findIndex((h) => h.heroId === cur);
        const next = heroes[(idx + ev.dir + heroes.length) % heroes.length]!;
        this.send({ type: 'selectHero', heroId: next.heroId });
        break;
      }
      case 'chooseSlot': {
        if (s.kind === 'Idle') break;
        this.state = { kind: 'SlotChosen', heroId: s.heroId, slot: ev.slot };   // a filled slot opens for replacement
        this.probe = undefined;
        break;
      }
      case 'chooseAction': {
        if (s.kind !== 'SlotChosen' && s.kind !== 'ActionChosen') break;
        const legalHere = this.ctx.actions(s.heroId, s.slot).some((a) => a.id === ev.action.id);
        if (!legalHere) break;
        if (ev.action.args === 'none') {
          this.place(s.heroId, s.slot, ev.action, { kind: 'none' });
          break;
        }
        this.state = { kind: 'ActionChosen', heroId: s.heroId, slot: s.slot, action: ev.action, pending: {} };
        break;
      }
      case 'hold': {
        if (s.kind === 'Idle' || s.kind === 'HeroSelected') break;
        this.place(s.heroId, s.slot, HOLD, { kind: 'none' });
        break;
      }
      case 'planBonus': {
        const hero = this.ctx.heroes().find((h) => h.heroId === ev.heroId);
        if (!hero) break;
        const v = this.ctx.legal(this.makeIntent(hero, 'B', ev.action, { kind: 'none' }));
        if (!v.legal) { this.emit({ type: 'announce', text: `${ev.action.name}: ${reasonText(v.reason)}` }); break; }
        this.place(hero.heroId, 'B', ev.action, { kind: 'none' });
        break;
      }
      case 'useItem': {
        // docs/40 IU7: Use Item goes in the Bonus slot like any action; a Salve then asks whom
        if (s.kind === 'Idle') break;
        const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
        const def = this.ctx.actions(s.heroId, 'B').find((a) => a.args === 'item');
        if (!hero) break;
        if (!def) { this.emit({ type: 'announce', text: `${hero.name} has nothing to use` }); break; }
        if (!ev.friend) {
          const v = this.ctx.legal(this.makeIntent(hero, 'B', def, { kind: 'item', item: ev.item }));
          if (!v.legal) { this.emit({ type: 'announce', text: `${def.name}: ${reasonText(v.reason)}` }); break; }
          this.place(hero.heroId, 'B', def, { kind: 'item', item: ev.item });
          break;
        }
        this.state = { kind: 'ActionChosen', heroId: hero.heroId, slot: 'B', action: def, pending: { item: ev.item } };
        this.cursor = { ...hero.tile };
        this.reprobe();
        break;
      }
      case 'hover': {
        this.cursor = ev.tile;
        this.reprobe();
        break;
      }
      case 'click': {
        this.cursor = ev.tile;
        if (s.kind === 'Idle') { this.send({ type: 'confirm', tile: ev.tile }); break; }
        if (s.kind === 'ActionChosen') {
          // a click on another of my heroes switches to it; otherwise the chosen action aims here
          const unit = this.targetAt(ev.tile, s.heroId, s.slot);
          const mine = unit !== undefined && !this.leavesBefore(ev.tile, unit, s) ? this.ctx.heroes().find((h) => h.unitId === unit && h.heroId !== s.heroId) : undefined;
          // ...unless the action is one that can be used on them (a heal, a ward, an item for a friend)
          if (mine && !this.aimsAtFriend(s.heroId, s.slot, s.action, mine.unitId)) { this.send({ type: 'selectHero', heroId: mine.heroId }); break; }
          // a Move past this pulse's reach walks there as a route (docs/149 MP)
          if (s.action.id === MOVE.id && this.probe && !this.probe.verdict.legal && this.walkFar(s.heroId, ev.tile)) break;
          this.send({ type: 'confirm', tile: ev.tile });
          if (this.state.kind === 'ActionChosen' && this.probe && !this.probe.verdict.legal) this.emit({ type: 'announce', text: `${s.action.name}: ${reasonText(this.probe.verdict.reason)}` });
          break;
        }
        // docs/63: with no action chosen a click into the never-seen says nothing of what is there;
        // docs/139 WD5: it walks there if a walk on open floor would, else does nothing
        if (this.ctx.unknown?.(ev.tile)) {
          const dark = this.darkWalk(ev.tile);
          if (dark?.kind === 'plan') {
            for (const intent of dark.intents) this.emit({ type: 'place', intent });
            this.emit({ type: 'announce', text: dark.label });
          }
          break;
        }
        const sug = this.suggest(ev.tile, true);
        // docs/46 T3: a click that would plan nothing — the hero's own square, open ground it cannot
        // walk to — lets go of the hero (a foe or a stash out of reach still says why instead)
        const aimed = this.ctx.unitAt(ev.tile) !== undefined && this.ctx.unitAt(ev.tile) !== this.ctx.heroes().find((h) => h.heroId === s.heroId)?.unitId;
        const nothingThere = this.ctx.stashAt?.(ev.tile) === undefined && !this.ctx.pouchAt?.(ev.tile) && !this.ctx.fixtureAt?.(ev.tile);
        // docs/149 MP: open ground past this pulse's reach is a walk over the pulses to come
        if ((!sug || sug.kind === 'blocked') && !aimed && nothingThere && this.walkFar(s.heroId, ev.tile)) break;
        if (!sug || (sug.kind === 'blocked' && !aimed && nothingThere)) {
          const name = this.ctx.heroes().find((h) => h.heroId === s.heroId)?.name;
          this.state = { kind: 'Idle' };
          this.probe = undefined;
          if (name) this.emit({ type: 'announce', text: `${name} deselected` });
          break;
        }
        if (sug.kind === 'select') { this.send({ type: 'selectHero', heroId: sug.heroId }); break; }
        if (sug.kind === 'blocked') { this.emit({ type: 'announce', text: sug.label }); break; }
        for (const intent of sug.intents) this.emit({ type: 'place', intent });
        this.emit({ type: 'announce', text: sug.label });
        this.state = { kind: 'HeroSelected', heroId: s.heroId };
        this.probe = undefined;
        this.suggestion = undefined;
        this.suggestKey = '';
        break;
      }
      case 'moveCursor': {
        this.cursor = {
          x: Math.min(this.bounds.w - 1, Math.max(0, this.cursor.x + ev.dx)),
          y: Math.min(this.bounds.h - 1, Math.max(0, this.cursor.y + ev.dy)),
        };
        this.reprobe();
        break;
      }
      case 'pickUnit': {
        if (s.kind !== 'ActionChosen') break;
        if (s.action.args === 'unit') { this.place(s.heroId, s.slot, s.action, { kind: 'unit', unit: ev.unit }); break; }
        if (s.action.args === 'unitDir') { this.state = { ...s, pending: { ...s.pending, unit: ev.unit } }; break; }
        break;
      }
      case 'pickDirection': {
        if (s.kind !== 'ActionChosen') break;
        if (s.action.args === 'direction') { this.place(s.heroId, s.slot, s.action, { kind: 'direction', dir: ev.dir }); break; }
        if (s.action.args === 'unitDir' && s.pending.unit !== undefined) {
          this.place(s.heroId, s.slot, s.action, { kind: 'unitDir', unit: s.pending.unit, dir: ev.dir });
        }
        break;
      }
      case 'confirm': {
        const tile = ev.tile ?? this.cursor;
        this.cursor = tile;
        if (s.kind === 'Idle') {
          // clicking one of my heroes selects it
          const unit = this.ctx.unitAt(tile);
          const hero = unit !== undefined ? this.ctx.heroes().find((h) => h.unitId === unit) : undefined;
          if (hero) this.send({ type: 'selectHero', heroId: hero.heroId });
          break;
        }
        if (s.kind === 'HeroSelected') {
          const unit = this.ctx.unitAt(tile);
          const hero = unit !== undefined ? this.ctx.heroes().find((h) => h.unitId === unit) : undefined;
          // Enter on a hero with nothing chosen does nothing: Lock plan (Space) is the only commitment
          if (hero && hero.heroId !== s.heroId) this.send({ type: 'selectHero', heroId: hero.heroId });
          break;
        }
        if (s.kind !== 'ActionChosen') break;
        switch (s.action.args) {
          case 'tile': {
            const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
            this.place(s.heroId, s.slot, s.action, { kind: 'tile', tile: hero && s.action.id === MOVE.id ? this.walkOrigin(hero, s.slot, tile) : tile });
            break;
          }
          case 'unit': case 'unitDir': {
            // a shove with its target picked: this click is the way it goes, from where the hero will stand
            if (s.action.args === 'unitDir' && s.pending.unit !== undefined) {
              const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
              const d = hero ? dirFrom(this.tileAt(hero, s.slot), tile) : undefined;
              if (d) this.send({ type: 'pickDirection', dir: d });
              break;
            }
            const unit = this.targetAt(tile, s.heroId, s.slot);
            if (unit !== undefined) this.send({ type: 'pickUnit', unit });
            // docs/114 MT4: an attack aimed at a thing lands on the thing; docs/115 ME5: a flask or an
            // elemental spell on bare ground lands there
            else if (s.action.args === 'unit' && (this.ctx.thingAt?.(tile) || s.action.ground)) this.place(s.heroId, s.slot, s.action, { kind: 'tile', tile });
            break;
          }
          case 'direction': {
            const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
            if (hero) { const d = dirFrom(this.tileAt(hero, s.slot), tile); if (d) this.send({ type: 'pickDirection', dir: d }); }
            break;
          }
          case 'line': {
            // A line starts on the chosen tile and runs away from the caster (from where it will stand, docs/81 SA2).
            const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
            if (hero) this.place(s.heroId, s.slot, s.action, { kind: 'line', from: tile, dir: dirFrom(this.tileAt(hero, s.slot), tile) ?? 'E' });
            break;
          }
          case 'interact': {
            const args = this.interactArgs(s.heroId, tile, s.slot);
            if (args) this.place(s.heroId, s.slot, s.action, args);
            break;
          }
          case 'item': {
            const unit = this.targetAt(tile, s.heroId, s.slot);
            const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
            if (unit !== undefined && hero && s.pending.item !== undefined) this.place(s.heroId, s.slot, s.action, itemArgs(s.pending.item, unit, hero.unitId));
            break;
          }
          case 'none': case 'raw': break;
        }
        break;
      }
      case 'escape': {
        if (s.kind === 'ActionChosen') this.state = { kind: 'SlotChosen', heroId: s.heroId, slot: s.slot };
        else if (s.kind === 'SlotChosen' || s.kind === 'Provisional') this.state = { kind: 'HeroSelected', heroId: s.heroId };
        else this.state = { kind: 'Idle' };
        this.probe = undefined;
        break;
      }
      case 'clear': {
        this.emit({ type: 'clear', heroId: ev.heroId, slot: ev.slot });
        break;
      }
    }
    // the suggestion follows the state (a new hero, slot or plan reads the square afresh)
    if (ev.type !== 'hover' && ev.type !== 'moveCursor') { this.suggestKey = ''; this.suggestion = this.quietSuggest(); }
    return this.state;
  }

  /** Candidate intent for the current ActionChosen state at the cursor, for the probe and the cursor chip. */
  /** docs/149 MP: a walk beyond this pulse's reach is sent as a route; true when it was. */
  private walkFar(heroId: number, tile: Tile): boolean {
    if (!this.ctx.routeTo?.(heroId, tile)) return false;
    this.emit({ type: 'route', heroId, tile });
    return true;
  }

  candidate(tile: Tile): Intent | undefined {
    const s = this.state;
    if (s.kind !== 'ActionChosen') return undefined;
    const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
    if (!hero) return undefined;
    const at = this.tileAt(hero, s.slot);
    switch (s.action.args) {
      case 'tile': return this.makeIntent(hero, s.slot, s.action, { kind: 'tile', tile: s.action.id === MOVE.id ? this.walkOrigin(hero, s.slot, tile) : tile });
      case 'unit': {
        const u = this.targetAt(tile, hero.heroId, s.slot);
        if (u === undefined) return this.ctx.thingAt?.(tile) || s.action.ground ? this.makeIntent(hero, s.slot, s.action, { kind: 'tile', tile }) : undefined;
        return this.makeIntent(hero, s.slot, s.action, { kind: 'unit', unit: u });
      }
      case 'unitDir': {
        const u = s.pending.unit ?? this.targetAt(tile, hero.heroId, s.slot);
        if (u === undefined) return undefined;
        const dir = s.pending.unit !== undefined ? dirFrom(at, tile) ?? 'E' : 'E';
        return this.makeIntent(hero, s.slot, s.action, { kind: 'unitDir', unit: u, dir });
      }
      case 'direction': { const d = dirFrom(at, tile); return d ? this.makeIntent(hero, s.slot, s.action, { kind: 'direction', dir: d }) : undefined; }
      case 'line': return this.makeIntent(hero, s.slot, s.action, { kind: 'line', from: tile, dir: dirFrom(at, tile) ?? 'E' });
      case 'none': return this.makeIntent(hero, s.slot, s.action, { kind: 'none' });
      case 'interact': { const args = this.interactArgs(hero.heroId, tile, s.slot); return args ? this.makeIntent(hero, s.slot, s.action, args) : undefined; }
      case 'item': {
        const u = this.targetAt(tile, hero.heroId, s.slot);
        return u === undefined || s.pending.item === undefined ? undefined : this.makeIntent(hero, s.slot, s.action, itemArgs(s.pending.item, u, hero.unitId));
      }
      case 'raw': return undefined;
    }
  }

  private reprobe(): void {
    const cand = this.candidate(this.cursor);
    const verdict = cand ? this.ctx.legal(cand) : undefined;
    // docs/63: a never-seen square keeps its secrets — a plan there is tried on the click, never explained
    this.probe = verdict && !this.ctx.unknown?.(this.cursor) ? { tile: this.cursor, verdict, ...(verdict.legal ? {} : { about: this.whatIsAt(this.cursor) }) } : undefined;
    this.suggestion = this.quietSuggest();
  }

  /** What a click at the cursor would do with no action chosen; over a never-seen square, only a walk into it. */
  private quietSuggest(): Suggestion | undefined {
    if (this.state.kind === 'ActionChosen' || this.state.kind === 'Provisional') return undefined;
    if (this.ctx.unknown?.(this.cursor)) return this.darkWalk(this.cursor);
    return this.suggest(this.cursor);
  }

  /**
   * docs/139 WD5: over a square the party has never seen, the selected hero's walk into it — the
   * sim judges it as open floor and plays it out as close as the real ground allows — and nothing
   * else: no words about the square, no reason when it cannot go.
   */
  private darkWalk(tile: Tile): Suggestion | undefined {
    const s = this.state;
    if (s.kind !== 'HeroSelected' && s.kind !== 'SlotChosen') return undefined;
    const hero = this.ctx.heroes().find((h) => h.heroId === s.heroId);
    if (!hero || this.ctx.committed(hero.heroId)) return undefined;
    const slot = s.kind === 'SlotChosen' ? s.slot : ([1, 2] as Slot[]).find((sl) => !this.ctx.filled(hero.heroId, sl));
    if (slot === undefined || !this.ctx.actions(hero.heroId, slot).some((a) => a.id === MOVE.id)) return undefined;
    const walk = this.makeIntent(hero, slot, MOVE, { kind: 'tile', tile: this.walkOrigin(hero, slot, tile) });
    if (!this.ctx.legal(walk).legal) return undefined;
    return { kind: 'plan', intents: [walk], label: slot === 2 ? 'Move on into the unknown (2nd action)' : 'Move into the unknown', short: 'Into the unknown', tone: 'move', walkTo: walk.args.kind === 'tile' ? walk.args.tile : tile };
  }

  /** Forget the cached suggestion (the plan or the board changed under it). */
  invalidate(): void { this.suggestKey = ''; this.reprobe(); }

  /**
   * docs/33 E1: what a click on `tile` means with this hero selected and no action chosen (in
   * SlotChosen, only that slot is used); with no hero selected, or nothing to plan there, what is on the square. Cached per
   * square until `invalidate()`.
   */
  suggest(tile: Tile, fresh = false): Suggestion | undefined {
    const s = this.state;
    if (s.kind === 'Idle') return this.describe(tile) ?? this.groundLine(tile);
    if (s.kind !== 'HeroSelected' && s.kind !== 'SlotChosen') return undefined;
    const key = `${s.kind}:${s.heroId}:${'slot' in s ? s.slot : ''}:${tile.x},${tile.y}`;
    if (!fresh && key === this.suggestKey) return this.suggestion;
    this.suggestKey = key;
    const act = this.act(s.heroId, s.kind === 'SlotChosen' ? s.slot : undefined, tile);
    // an action on the square still says what is on it, as with no hero selected
    if (act?.kind === 'plan') {
      const about = this.describe(tile);
      return about?.kind === 'blocked' && about.short ? { ...act, about: about.short } : act;
    }
    // nothing to plan here: the chip names what is on the square, never only why it can't be done
    // ("Out of reach"); the coach line keeps the why
    // (the hero's own square keeps the coach's own line: there is nothing new to name)
    const self = this.ctx.heroes().find((h) => h.heroId === s.heroId);
    if (self && (tileEq(self.tile, tile) || this.ctx.unitAt(tile) === self.unitId || this.ownSquare(self, s.kind === 'SlotChosen' ? s.slot : undefined, tile))) return act;
    const what = this.whatIsAt(tile);
    if (act?.kind === 'blocked') return what ? { ...act, short: what, label: named(act.label, what) ? act.label : `${what} — ${lower(act.label)}` } : act;
    return act ?? (what ? { kind: 'blocked', label: what, short: what } : undefined);
  }

  /** Whether `action`, chosen for `heroId`, may be used on the friendly `unit` (so a click there aims rather than selects). */
  private aimsAtFriend(heroId: number, slot: Slot, action: ActionDef, unit: number): boolean {
    if (action.args === 'item') return true;
    // a mend, ward or guard is for friends: it aims even when the friend is out of reach (and says so)
    const support = action.glyph === 'heal' || action.glyph === 'ward' || action.glyph === 'shield' || action.glyph === 'guard';
    if (support && action.args !== 'none') return true;
    const hero = this.ctx.heroes().find((h) => h.heroId === heroId);
    if (!hero || (action.args !== 'unit' && action.args !== 'unitDir')) return false;
    // anything else aimed at a unit, only when the rules would take this friend as its target
    return this.ctx.legal(this.makeIntent(hero, slot, action, { kind: 'unit', unit })).legal;
  }

  /** What is on `tile`, in words: a unit's name, a stash, a mechanism's line, else the ground. */
  whatIsAt(tile: Tile): string | undefined {
    const unit = this.ctx.unitAt(tile);
    if (unit !== undefined) {
      const name = cap(this.ctx.heroes().find((h) => h.unitId === unit)?.name ?? this.ctx.unitName?.(unit) ?? 'someone');
      const note = this.ctx.unitNote?.(unit);
      return note ? `${name} · ${note}` : name;
    }
    const d = this.describe(tile);
    const ground = this.ctx.groundAt?.(tile);
    return (d?.kind === 'blocked' ? d.short : undefined) ?? (ground ? cap(ground) : undefined);
  }

  /** A bare square with no hero selected: what the ground is. */
  private groundLine(tile: Tile): Suggestion | undefined {
    const g = this.ctx.groundAt?.(tile);
    return g ? { kind: 'blocked', label: cap(g), short: cap(g) } : undefined;
  }

  /** What a click on `tile` means for this hero (`pinned`: the slot chosen, if any). */
  private act(heroId: number, pinned: Slot | undefined, tile: Tile): Suggestion | undefined {
    const hero = this.ctx.heroes().find((h) => h.heroId === heroId);
    if (!hero) return undefined;
    const unit = this.ctx.unitAt(tile);
    // another of my heroes is always picked up, locked plan or not
    const mine = unit !== undefined && unit !== hero.unitId ? this.ctx.heroes().find((h) => h.unitId === unit) : undefined;
    if (mine) return { kind: 'select', heroId: mine.heroId, label: `Select ${mine.name}` };
    if (this.ctx.committed(hero.heroId)) return { kind: 'blocked', label: 'Plan locked — Unlock plan to change it', short: 'Plan locked' };
    const free = pinned !== undefined ? [pinned] : ([1, 2] as Slot[]).filter((sl) => !this.ctx.filled(hero.heroId, sl));
    if (unit !== undefined && unit !== hero.unitId) {
      if ((this.ctx.sideOf?.(unit) ?? 'foe') === 'foe') return this.strike(hero, unit, free, pinned === undefined);
      return this.describe(tile);
    }
    // docs/81 SA2: the hero's own square is where it will stand as its next slot acts — after a
    // planned walk, its ghost; the square it leaves is open ground to walk back to
    if (this.ownSquare(hero, pinned, tile)) {
      // docs/41: a rune or lever under the hero's own feet is worked where it stands
      const under = this.ctx.fixtureAt?.(tile);
      return under && fixtureVerb(under, this.tileAt(hero, this.nextSlot(hero, pinned)), this.ctx.dragMode?.(), hero.unitId) !== undefined ? this.work(hero, under, free, false) : undefined;
    }
    const stash = this.ctx.stashAt?.(tile);
    if (stash !== undefined) return this.open(hero, stash, tile, free, pinned === undefined);
    // docs/114 MT4: a thing on the field is used: kicked, lit, rung or rolled from beside it, else shot
    const thing = this.ctx.thingAt?.(tile);
    if (thing) return this.useThing(hero, thing, free, pinned === undefined);
    const fx = this.ctx.fixtureAt?.(tile);
    // docs/52 HT5: a rogue clicking a trap (or anything that might be one) disarms it
    if (fx && trapLive(fx)) { const d = this.disarm(hero, fx, free, pinned === undefined); if (d) return d; }
    // docs/140 DT11: a found plate or wire of a dungeon trap is a rogue's to take apart too
    const snare = this.ctx.snareAt?.(tile);
    if (snare) { const d = this.disarm(hero, { tile, name: snare.name }, free, pinned === undefined); if (d) return d; }
    if (fx && fixtureVerb(fx, this.tileAt(hero, free[0] ?? 1), this.ctx.dragMode?.(), hero.unitId) !== undefined) return this.work(hero, fx, free, pinned === undefined);
    if (!free.length) return { kind: 'blocked', label: 'Both actions are planned — Undo, or click a slot to replan it' };
    const slot = free[0]!;
    if (!this.ctx.actions(hero.heroId, slot).some((a) => a.id === MOVE.id)) return { kind: 'blocked', label: `Move does not fit slot ${slot}` };
    const walk = this.makeIntent(hero, slot, MOVE, { kind: 'tile', tile: this.walkOrigin(hero, slot, tile) });
    const v = this.ctx.legal(walk);
    // nowhere to walk: say what is on the square, as with no hero selected; a bare wall says nothing
    if (!v.legal) return this.describe(tile) ?? (/NoPath/.test(v.reason) ? undefined : { kind: 'blocked', label: `Can't move there: ${reasonText(v.reason)}`, short: cap(reasonText(v.reason)) });
    // docs/52: a trap the party has seen through is a choice, not an accident
    if (fx?.kind === 'Trap' && fx.known && fx.armed) return { kind: 'plan', intents: [walk], label: `Step onto ${secretName(fx)} — it will spring`, short: 'Onto the trap', tone: 'attack', walkTo: walk.args.kind === 'tile' ? walk.args.tile : tile };
    if (snare) return { kind: 'plan', intents: [walk], label: `Step onto ${snare.name} — the trap will spring`, short: 'Onto the trap', tone: 'attack', walkTo: walk.args.kind === 'tile' ? walk.args.tile : tile };
    if (this.ctx.pouchAt?.(tile)) return { kind: 'plan', intents: [walk], label: this.ctx.findAt?.(tile) ? 'Walk over · pick it up (finders keepers: it is this hero’s)' : 'Walk over · pick up the pouch', short: 'Pick up', tone: 'move', walkTo: walk.args.kind === 'tile' ? walk.args.tile : tile };
    return { kind: 'plan', intents: [walk], label: slot === 2 ? 'Move on (2nd action)' : 'Move here', tone: 'move', walkTo: walk.args.kind === 'tile' ? walk.args.tile : tile };
  }

  /**
   * docs/79: where a Move toward the pointed square stands a huge body — the reachable origin whose
   * footprint covers it, nearest the square (`originFor`); the square itself for one-square heroes
   * or when no stop covers it (the sim then says why it can't go).
   */
  private walkOrigin(hero: HeroInfo, slot: Slot, tile: Tile): Tile {
    const size = sizeOf(hero);
    if (size <= 1 || !this.ctx.stops) return tile;
    const stops = new Map(this.ctx.stops(hero.heroId, slot).map((st) => [`${st.tile.x},${st.tile.y}`, st.cost]));
    return originFor(tile, size, (t) => stops.get(`${t.x},${t.y}`)) ?? tile;
  }

  /** No hero selected: one of mine selects, anyone else is named, a stash or mechanism reads its inspect line. */
  private describe(tile: Tile): Suggestion | undefined {
    const unit = this.ctx.unitAt(tile);
    if (unit !== undefined) {
      const mine = this.ctx.heroes().find((h) => h.unitId === unit);
      if (mine) return { kind: 'select', heroId: mine.heroId, label: `Select ${mine.name}` };
      const name = cap(this.ctx.unitName?.(unit) ?? 'someone');
      return { kind: 'blocked', label: (this.ctx.sideOf?.(unit) ?? 'foe') === 'foe' ? `${name} — pick a hero to attack it` : name, short: name };
    }
    if (this.ctx.stashAt?.(tile) !== undefined) return { kind: 'blocked', label: 'A stash — pick a hero to open it', short: 'A stash' };
    if (this.ctx.findAt?.(tile)) return { kind: 'blocked', label: 'Something lying here — walk a hero onto it to pick it up (finders keepers)', short: 'A find' };
    if (this.ctx.pouchAt?.(tile)) return { kind: 'blocked', label: 'A fallen foe\'s pouch — walk a hero onto it to pick it up', short: 'A pouch' };
    const fx = this.ctx.fixtureAt?.(tile);
    if (fx) return { kind: 'blocked', label: secretLine(fx), short: secretLine(fx) };
    // docs/140 DT11: a found plate or wire names itself
    const snare = this.ctx.snareAt?.(tile);
    if (snare) { const line = `${cap(snare.name)} — a ${snare.wire ? 'tripwire' : 'pressure plate'}, found; it springs a trap. A rogue can disarm it`; return { kind: 'blocked', label: line, short: line }; }
    return undefined;
  }

  /** A foe: the cheapest attack that lands from where the hero will stand, else close in and attack. */
  private strike(hero: HeroInfo, unit: number, free: Slot[], mayApproach: boolean): Suggestion {
    const name = this.ctx.unitName?.(unit) ?? 'the foe';
    if (!free.length) return { kind: 'blocked', label: 'Both actions are planned — Undo to attack instead', target: unit };
    let why: string | undefined;
    for (const slot of free) {
      for (const a of attacksFor(this.ctx.actions(hero.heroId, slot))) {
        const hit = this.makeIntent(hero, slot, a, { kind: 'unit', unit });
        const v = this.ctx.legal(hit);
        if (v.legal) return { kind: 'plan', intents: [hit], label: `${a.name} ${name}`, short: a.name, tone: 'attack', target: unit };
        why ??= reasonText(v.reason);
      }
    }
    // two actions free: walk to the cheapest square the attack lands from, then strike
    if (mayApproach && free.includes(1) && free.includes(2) && this.ctx.legalAfter && this.ctx.stops && this.ctx.actions(hero.heroId, 1).some((a) => a.id === MOVE.id)) {
      const attacks = attacksFor(this.ctx.actions(hero.heroId, 2)).slice(0, 2);
      for (const st of this.ctx.stops(hero.heroId, 1).slice(0, 80)) {
        const walk = this.makeIntent(hero, 1, MOVE, { kind: 'tile', tile: st.tile });
        for (const a of attacks) {
          const hit = this.makeIntent(hero, 2, a, { kind: 'unit', unit });
          if (this.ctx.legalAfter(hit, [walk]).legal) return { kind: 'plan', intents: [walk, hit], label: `Close in · ${a.name} ${name}`, short: `Close in · ${a.name}`, tone: 'attack', walkTo: st.tile, target: unit };
        }
      }
      return { kind: 'blocked', label: `${name} is out of reach this turn`, short: 'Out of reach', target: unit };
    }
    return { kind: 'blocked', label: `Can't attack ${name}: ${why ?? 'no attack fits'}`, short: cap(why ?? 'no attack fits'), target: unit };
  }

  /** A found stash: open it from here, else walk next to it and open it. */
  private open(hero: HeroInfo, fixture: number, tile: Tile, free: Slot[], mayApproach: boolean): Suggestion {
    if (!free.length) return { kind: 'blocked', label: 'Both actions are planned — Undo to open it' };
    for (const slot of free) {
      const a = this.ctx.actions(hero.heroId, slot).find((x) => x.args === 'interact');
      if (!a) continue;
      const it = this.makeIntent(hero, slot, a, { kind: 'interact', fixture, verb: 'Open' });
      if (this.ctx.legal(it).legal) return { kind: 'plan', intents: [it], label: 'Open the stash', tone: 'support' };
    }
    const a = this.ctx.actions(hero.heroId, 2).find((x) => x.args === 'interact');
    if (mayApproach && a && free.includes(1) && free.includes(2) && this.ctx.legalAfter && this.ctx.stops) {
      const near = this.ctx.stops(hero.heroId, 1).filter((st) => gapTo(st.tile, sizeOf(hero), tile) <= 1);
      for (const st of near) {
        const walk = this.makeIntent(hero, 1, MOVE, { kind: 'tile', tile: st.tile });
        const it = this.makeIntent(hero, 2, a, { kind: 'interact', fixture, verb: 'Open' });
        if (this.ctx.legalAfter(it, [walk]).legal) return { kind: 'plan', intents: [walk, it], label: 'Walk over · open the stash', tone: 'support', walkTo: st.tile };
      }
    }
    return { kind: 'blocked', label: 'The stash is out of reach this turn' };
  }

  /**
   * docs/52 HT5: a trap and a hero who can Disarm — from beside it, else walk beside it and
   * disarm. `undefined` when the hero has no Disarm, so the click falls through to a walk.
   */
  private disarm(hero: HeroInfo, fx: Pick<FixtureView, 'tile' | 'name'>, free: Slot[], mayApproach: boolean): Suggestion | undefined {
    const pick = (slot: Slot) => this.ctx.actions(hero.heroId, slot).find((x) => x.id === DISARM_ID);
    if (!pick(1) && !pick(2)) return undefined;
    const what = fx.name || 'it';
    if (!free.length) return { kind: 'blocked', label: `Both actions are planned — Undo to disarm ${what}` };
    for (const slot of free) {
      const a = pick(slot);
      if (!a) continue;
      const it = this.makeIntent(hero, slot, a, { kind: 'tile', tile: fx.tile });
      if (this.ctx.legal(it).legal) return { kind: 'plan', intents: [it], label: `Disarm ${what}`, short: 'Disarm', tone: 'support' };
    }
    const a = pick(2);
    if (mayApproach && a && free.includes(1) && free.includes(2) && this.ctx.legalAfter && this.ctx.stops) {
      for (const st of this.ctx.stops(hero.heroId, 1).filter((x) => gapTo(x.tile, sizeOf(hero), fx.tile) === 1)) {
        const walk = this.makeIntent(hero, 1, MOVE, { kind: 'tile', tile: st.tile });
        const it = this.makeIntent(hero, 2, a, { kind: 'tile', tile: fx.tile });
        if (this.ctx.legalAfter(it, [walk]).legal) return { kind: 'plan', intents: [walk, it], label: `Walk beside · disarm ${what}`, short: 'Walk · Disarm', tone: 'support', walkTo: st.tile };
      }
    }
    return { kind: 'blocked', label: `${cap(what)} is out of reach this turn — stand beside it to disarm it`, short: 'Out of reach' };
  }

  /**
   * docs/114 MT4: a thing on the field. From beside it the hero puts a hand to it (Shift picks the
   * other hand a thing answers: kick the brazier over, or douse it); from afar a hero who can strike
   * it shoots or hurls at it; else the hero walks over and does it next.
   */
  private useThing(hero: HeroInfo, thing: ThingView, free: Slot[], mayApproach: boolean): Suggestion {
    const what = describeThing(thing);
    if (!free.length) return { kind: 'blocked', label: `Both actions are planned — Undo to use the ${what}` };
    const verbs = thingVerbs(thing.kind, thing.lit);
    const order = this.ctx.dragMode?.() && verbs.length > 1 ? [...verbs.slice(1), verbs[0]!] : verbs;
    for (const slot of free) {
      const a = this.ctx.actions(hero.heroId, slot).find((x) => x.args === 'interact');
      if (!a) continue;
      for (const verb of order) {
        const it = this.makeIntent(hero, slot, a, { kind: 'interact', fixture: thing.id, verb });
        if (this.ctx.legal(it).legal) return { kind: 'plan', intents: [it], label: thingLabel(thing, verb), short: thingShort(thing, verb), tone: 'support' };
      }
    }
    // from afar: the cheapest attack that can reach it
    for (const slot of free) {
      for (const a of attacksFor(this.ctx.actions(hero.heroId, slot))) {
        const hit = this.makeIntent(hero, slot, a, { kind: 'tile', tile: thing.tile });
        if (this.ctx.legal(hit).legal) return { kind: 'plan', intents: [hit], label: `${a.name} the ${what}`, short: a.name, tone: 'attack' };
      }
    }
    const a = this.ctx.actions(hero.heroId, 2).find((x) => x.args === 'interact');
    if (mayApproach && a && order.length && free.includes(1) && free.includes(2) && this.ctx.legalAfter && this.ctx.stops) {
      for (const st of this.ctx.stops(hero.heroId, 1).filter((x) => gapTo(x.tile, sizeOf(hero), thing.tile) === 1)) {
        const walk = this.makeIntent(hero, 1, MOVE, { kind: 'tile', tile: st.tile });
        for (const verb of order) {
          const it = this.makeIntent(hero, 2, a, { kind: 'interact', fixture: thing.id, verb });
          if (this.ctx.legalAfter(it, [walk]).legal) return { kind: 'plan', intents: [walk, it], label: `Walk over · ${lower(thingLabel(thing, verb))}`, short: `Walk · ${thingShort(thing, verb)}`, tone: 'support', walkTo: st.tile };
        }
      }
    }
    return { kind: 'blocked', label: `${cap(what)} — out of reach this turn`, short: cap(what) };
  }

  /** The Interact a chosen action aims at `tile`: a stash opened, else a mechanism worked. */
  private interactArgs(heroId: number, tile: Tile, slot: Slot): ActionArgs | undefined {
    const stash = this.ctx.stashAt?.(tile);
    if (stash !== undefined) return { kind: 'interact', fixture: stash, verb: 'Open' };
    const thing = this.ctx.thingAt?.(tile);
    const tv = thing ? thingVerbs(thing.kind, thing.lit) : [];
    if (thing && tv.length) return { kind: 'interact', fixture: thing.id, verb: this.ctx.dragMode?.() && tv.length > 1 ? tv[1]! : tv[0]! };
    const fx = this.ctx.fixtureAt?.(tile);
    const hero = this.ctx.heroes().find((h) => h.heroId === heroId);
    const verb = fx && hero ? fixtureVerb(fx, this.tileAt(hero, slot), this.ctx.dragMode?.(), hero.unitId) : undefined;
    return fx && verb ? { kind: 'interact', fixture: fx.id, verb } : undefined;
  }

  /**
   * docs/41: a mechanism — work it from here, else walk beside it and work it. A block is pushed
   * away from where the hero stands (dragged toward it with Shift), so the label names the way.
   */
  private work(hero: HeroInfo, fx: FixtureView, free: Slot[], mayApproach: boolean): Suggestion {
    const what = fixtureNoun(fx);
    const drag = this.ctx.dragMode?.() ?? false;
    if (!free.length) return { kind: 'blocked', label: `Both actions are planned — Undo to work ${what}` };
    let why: string | undefined;
    for (const slot of free) {
      const a = this.ctx.actions(hero.heroId, slot).find((x) => x.args === 'interact');
      const from = this.tileAt(hero, slot);
      if (!a) continue;
      for (const verb of fixtureVerbs(fx, from, drag, hero.unitId)) {
        const it = this.makeIntent(hero, slot, a, { kind: 'interact', fixture: fx.id, verb });
        const v = this.ctx.legal(it);
        if (v.legal) return { kind: 'plan', intents: [it], label: fixtureLabel(fx, verb, from), short: fixtureShort(fx, verb, from), tone: 'support' };
        why ??= interactWhy(v.reason, fx);
      }
    }
    const a = this.ctx.actions(hero.heroId, 2).find((x) => x.args === 'interact');
    if (mayApproach && a && free.includes(1) && free.includes(2) && this.ctx.legalAfter && this.ctx.stops) {
      const near = this.ctx.stops(hero.heroId, 1).filter((st) => gapTo(st.tile, sizeOf(hero), fx.tile) <= 1);
      if (!near.length) return { kind: 'blocked', label: `${cap(what)} is out of reach this turn — walk closer first`, short: 'Out of reach' };
      why = undefined;
      for (const st of near) {
        const walk = this.makeIntent(hero, 1, MOVE, { kind: 'tile', tile: st.tile });
        for (const verb of fixtureVerbs(fx, st.tile, drag, hero.unitId)) {
          const it = this.makeIntent(hero, 2, a, { kind: 'interact', fixture: fx.id, verb });
          const v = this.ctx.legalAfter(it, [walk]);
          if (v.legal) return { kind: 'plan', intents: [walk, it], label: `Walk over · ${lower(fixtureLabel(fx, verb, st.tile))}`, short: `Walk · ${fixtureShort(fx, verb, st.tile)}`, tone: 'support', walkTo: st.tile };
          why ??= interactWhy(v.reason, fx);
        }
      }
      return { kind: 'blocked', label: why ? `${cap(what)}: ${why}` : `${cap(what)} is out of reach this turn`, short: why ? cap(why) : 'Out of reach' };
    }
    return { kind: 'blocked', label: `${cap(what)}: ${why ?? 'out of reach this turn'}`, short: cap(why ?? 'out of reach') };
  }

  /** The slot a click with no action chosen plans into: the pinned one, else the first free, else the Bonus. */
  private nextSlot(hero: HeroInfo, pinned: Slot | undefined): Slot {
    return pinned ?? ([1, 2] as Slot[]).find((sl) => !this.ctx.filled(hero.heroId, sl)) ?? 'B';
  }

  /** Whether `tile` is under the hero where it will stand as its next slot acts (its ghost, after a planned walk). */
  private ownSquare(hero: HeroInfo, pinned: Slot | undefined, tile: Tile): boolean {
    return tilesAt(this.tileAt(hero, this.nextSlot(hero, pinned)), sizeOf(hero)).some((t) => tileEq(t, tile));
  }

  /** docs/81 SA2: where the hero will stand as `slot` begins (its earlier slots walked), else where it stands now. */
  tileAt(hero: HeroInfo, slot: Slot): Tile {
    return this.ctx.standsAt?.(hero.heroId, slot).find((x) => x.unit === hero.unitId)?.tile ?? hero.tile;
  }

  /**
   * docs/81 SA3: the unit a click on `tile` aims at for this hero's `slot` — whoever will stand
   * there then (a friend's planning ghost, a foe the party drops first), else whoever stands there
   * now, so a click on the figure itself still aims at it.
   */
  /**
   * A walk (any square-aimed action) at a square whose occupant will have stepped off it before this
   * slot acts: the square is the aim, not them (Nyx steps out ahead of Bram: his walk onto her square,
   * lit as a stop, plans rather than switching to her). A body still on its own origin then has not moved.
   */
  private leavesBefore(tile: Tile, unit: number, s: { heroId: number; slot: Slot; action: ActionDef }): boolean {
    if (s.action.args !== 'tile') return false;
    const then = this.ctx.standsAt?.(s.heroId, s.slot).find((x) => x.unit === unit);
    return !!then && !then.fallen && !tileEq(then.tile, tile) && this.ctx.unitAt(then.tile) !== unit;
  }

  targetAt(tile: Tile, heroId: number, slot: Slot): number | undefined {
    const stands = this.ctx.standsAt?.(heroId, slot) ?? [];
    return stands.find((x) => tileEq(x.tile, tile))?.unit ?? this.ctx.unitAt(tile);
  }

  private place(heroId: number, slot: Slot, action: ActionDef, args: ActionArgs): void {
    const hero = this.ctx.heroes().find((h) => h.heroId === heroId);
    if (!hero) return;
    const intent = this.makeIntent(hero, slot, action, args);
    const verdict = this.ctx.legal(intent);
    if (!verdict.legal) {
      // docs/63: aimed into the never-seen, it simply does not take (why would tell what is there)
      this.probe = this.ctx.unknown?.(this.cursor) ? undefined : { tile: this.cursor, verdict, about: this.whatIsAt(this.cursor) };
      return;                                              // stays in ActionChosen with the reason in the cursor chip
    }
    this.state = { kind: 'Provisional', heroId, slot, intent };
    this.emit({ type: 'place', intent });
    this.emit({ type: 'announce', text: `${hero.name}: ${action.name} in slot ${slot}` });
    // Provisional is transient: the intent is in `local`; authoring continues on the hero.
    this.state = { kind: 'HeroSelected', heroId };
    this.probe = undefined;
  }

  private makeIntent(hero: HeroInfo, slot: Slot, action: ActionDef, args: ActionArgs, substituted = false): Intent {
    return { heroId: hero.heroId, unit: hero.unitId, slot, action: action.id, args, snapshotHash: this.ctx.snapshotHash(), revision: this.revision++, substituted };
  }
}

/** Use Item's args: the user itself carries no target. */
function itemArgs(item: number, unit: number, self: number): ActionArgs {
  return unit === self ? { kind: 'item', item } : { kind: 'item', item, target: unit };
}

/** The attacks a click on a foe may use, the plain one first (free, no cooldown, the weapon's own). */
/** docs/114 MT4: what a hand does to a thing, in words: "Kick over the brazier", "Roll the boulder". */
export function thingLabel(t: ThingView, verb: InteractVerb): string {
  const what = describeThing(t);
  const rolls = t.kind === 'Boulder' || t.kind === 'Log' || t.kind === 'MineCart' || t.kind === 'Cart';
  const rings = t.kind === 'Bell' || t.kind === 'Gong' || t.kind === 'Anvil';
  const hangs = t.kind === 'Chandelier' || t.kind === 'Icicles';
  switch (verb) {
    case 'Push': return rolls ? `Roll the ${what}` : `Tip over the ${what}`;
    case 'Touch': return rings ? `Ring the ${what}` : t.lit ? `Put out the ${what}` : `Light the ${what}`;
    case 'Work': return hangs ? `Bring down the ${what}` : t.kind === 'Well' ? 'Slosh water from the well' : t.kind === 'HolyFont' ? 'Scoop out holy water' : `Smash the ${what}`;
    default: return `${verb} the ${what}`;
  }
}

/** The chip's short word for a hand on a thing. */
export function thingShort(t: ThingView, verb: InteractVerb): string {
  return thingLabel(t, verb).split(' the ')[0]!;
}

export function attacksFor(actions: readonly ActionDef[]): ActionDef[] {
  return actions.filter((a) => a.args === 'unit' && (a.glyph === 'attack' || a.glyph === 'beam') && !a.passive)
    .sort((a, b) => (a.cost ?? 0) - (b.cost ?? 0) || (a.cooldown ?? 0) - (b.cooldown ?? 0) || Number(!!b.missile) - Number(!!a.missile) || a.id - b.id);
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const lower = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
/** The line already names the thing (by its head: "the beetle dial", not its look as well). */
const named = (label: string, what: string) => label.toLowerCase().includes(what.split(' — ')[0]!.trim().toLowerCase());

/**
 * docs/41: the verb a click works a mechanism with, from `from`: a lever and the bolt are
 * pulled, a block pushed (or dragged), a statue or an unlit rune touched, an inscription read.
 * Plates, gates and a sigil that judges by touch take no verb (a plate is stood on).
 */
export function fixtureVerb(f: FixtureView, from: Tile, drag = false, who?: number): InteractVerb | undefined {
  switch (f.kind) {
    case 'Lever': return 'Pull';
    case 'Block': return drag ? 'Pull' : 'Push';
    case 'Statue': return 'Touch';
    case 'Rune': return f.on ? undefined : 'Touch';
    case 'Inscription': return 'Examine';
    case 'Lock': return (f.lock === 'Pattern' || f.lock === 'Tally' || f.lock === 'Offering' || f.lock === 'Arrange' || f.lock === 'Measure' || f.lock === 'Machine') && !f.on ? 'Pull' : undefined;
    // docs/106: a lock-book piece is touched (a lit one sets down again, or tells the lock it is done)
    case 'Prop': case 'Switch': case 'Jug': case 'Token': return 'Touch';
    // docs/52: a found hidden way opens like a door, from beside it
    case 'Passage': return f.on || (from.x === f.tile.x && from.y === f.tile.y) ? undefined : 'Open';
    // docs/134 HL5: a plain shut door opens from beside it; one a mechanism drives (`needed`) does not
    case 'Door': return f.on || f.needed ? undefined : 'Open';
    // docs/76 EE3: a found hidden door opens like a way; an open stairway down is taken by touch
    case 'HiddenDoor':
      if (from.x === f.tile.x && from.y === f.tile.y) return undefined;
      if (!f.on) return 'Open';
      return f.below !== undefined ? 'Touch' : undefined;
    // docs/55
    case 'Mirror': case 'Valve': case 'Dial': case 'Turntable': return 'Turn';
    // docs/105: a bearer takes what is carried to it (or gives back what it holds); a thing to give is taken up
    case 'Bearer': return 'Place';
    case 'Idol': return f.holder === undefined && !f.on ? 'Take' : undefined;
    case 'Brazier': return 'Touch';
    case 'Bell': return f.on ? undefined : 'Touch';
    case 'Portal': return f.on ? 'Touch' : undefined;
    case 'Key': return f.holder === undefined && !f.on ? 'Take' : undefined;
    case 'Socket': return f.on ? undefined : 'Place';
    // docs/50: take up the relic lying there, put a blow into the task
    case 'Relic': return f.on ? undefined : 'Take';
    case 'Task': return f.on ? undefined : 'Work';
    // docs/60: a stair is taken by touch from beside it; a shrine touched while it has uses
    case 'Stair': return 'Touch';
    case 'Shrine': return (f.uses ?? 0) > 0 ? 'Touch' : undefined;
    // docs/61 EX6, docs/138 XO5: a wonder is beheld (Examine) from beside it, once by each hero
    case 'Wonder': return who !== undefined && f.blessed?.includes(who) ? undefined : 'Examine';
    // docs/140 DT3: a curio answers its one verb (pulled, taken, sat in…), once
    case 'Curio': return f.on || !f.curio ? undefined : curioVerb(f.curio);
    default: return undefined;
  }
}

/**
 * The verbs a click tries on a mechanism, in turn: a block the hero cannot push from where it
 * stands (a wall or a corner behind it) is dragged instead, so no Shift is needed to free it.
 */
export function fixtureVerbs(f: FixtureView, from: Tile, drag = false, who?: number): InteractVerb[] {
  if (f.kind === 'Block') return drag ? ['Pull', 'Push'] : ['Push', 'Pull'];
  const verb = fixtureVerb(f, from, drag, who);
  return verb ? [verb] : [];
}

/** `fk_grammar::AbilityId::DISARM` (docs/52 HT5). */
export const DISARM_ID = 27;

const COMPASS: Record<Direction, string> = { N: 'north', E: 'east', S: 'south', W: 'west', NE: 'north', SE: 'south', SW: 'south', NW: 'north' };

/** "the heron statue" from "the heron statue — green bronze, holding a lamp". */
export function fixtureNoun(f: FixtureView): string {
  const head = f.name.split(' — ')[0]!.trim();
  if (f.kind === 'Inscription') return 'the inscription';
  if (f.kind === 'Stair') return f.down ? 'the stair down' : 'the stair up';
  if (f.kind === 'HiddenDoor') return f.below !== undefined ? 'the hidden stairway' : 'the hidden door';
  if (f.kind === 'Curio') return curioNoun(f);
  return head || 'it';
}

/** What a click will do to the mechanism, in words (the cursor chip and the announcement). */
export function fixtureLabel(f: FixtureView, verb: InteractVerb, from: Tile): string {
  const what = fixtureNoun(f);
  switch (f.kind) {
    case 'Lever': return `${f.on ? 'Lower' : 'Raise'} ${what}`;
    case 'Block': {
      const away = dirFrom(from, f.tile);
      if (verb === 'Pull') { const back = away ? dirFrom(f.tile, from) : undefined; return `Drag ${what}${back ? ` ${COMPASS[back]}` : ''} (step back)`; }
      return `Push ${what}${away ? ` ${COMPASS[away]}` : ''}`;
    }
    case 'Statue': return `Touch ${what}`;
    case 'Rune': return `Wake ${what}`;
    case 'Inscription': return `Read ${f.name || 'the inscription'}`;
    case 'Passage': case 'Door': return `Open ${what}`;
    case 'HiddenDoor': return verb === 'Touch' ? `Go down ${what}` : `Open ${what}`;
    case 'Lock': return `Draw ${what} (tries the ${f.lock === 'Offering' ? 'figures as they hold' : f.lock === 'Tally' ? 'dials as they stand, and counts' : 'levers or dials as they stand'})`;
    case 'Mirror': return `Turn ${what}`;
    case 'Valve': return `${f.on ? 'Shut' : 'Open'} ${what}`;
    case 'Dial': return `Turn ${what} to its next face`;
    case 'Turntable': return `Turn ${what} to look at the next`;
    case 'Prop': return f.on ? `Touch ${what} (it is taken up: touch another to swap, or it to set it down)` : `Touch ${what}`;
    case 'Switch': return `Turn ${what} ${f.on ? 'off' : 'on'}`;
    case 'Jug': return f.on ? `Set ${what} down` : `Take up ${what} to pour (then touch the jug to pour it into)`;
    case 'Token': return `Work ${what}`;
    case 'Bearer': return f.on ? `Take from ${what} (or swap what you carry)` : `Give ${what} what you carry`;
    case 'Idol': return `Take up ${what}`;
    case 'Brazier': return `${f.on ? 'Douse' : 'Light'} ${what}`;
    case 'Bell': return `Ring ${what}`;
    case 'Portal': return `Step through ${what}`;
    case 'Key': return `Take ${what}`;
    case 'Socket': return `Set the key in ${what}`;
    case 'Relic': return `Take up ${what}`;
    case 'Task': return `Work at ${what} (${(f.done ?? 0) + 1} of ${f.need ?? 1})`;
    case 'Stair': return `Take ${what} (to another depth)`;
    case 'Shrine': return `Pray at ${what} — it ${f.boon ? BOON_WORDS[f.boon].verb : 'answers'}${(f.uses ?? 0) > 0 ? ` (${f.uses} left)` : ''}`;
    case 'Wonder': return `Behold ${what}`;
    case 'Curio': return curioLabel(f);
    default: return `Work ${what}`;
  }
}

function fixtureShort(f: FixtureView, verb: InteractVerb, from: Tile): string {
  switch (f.kind) {
    case 'Lever': return f.on ? 'Lower' : 'Raise';
    case 'Block': { const d = verb === 'Pull' ? dirFrom(f.tile, from) : dirFrom(from, f.tile); return `${verb === 'Pull' ? 'Drag' : 'Push'}${d ? ` ${COMPASS[d]}` : ''}`; }
    case 'Statue': return 'Touch';
    case 'Rune': return 'Wake';
    case 'Inscription': return 'Read';
    case 'Lock': return 'Draw bolt';
    case 'Passage': case 'Door': return 'Open';
    case 'HiddenDoor': return verb === 'Touch' ? 'Go down' : 'Open';
    case 'Mirror': case 'Dial': case 'Turntable': return 'Turn';
    case 'Bearer': return f.on ? 'Swap' : 'Give';
    case 'Prop': return 'Touch';
    case 'Switch': return f.on ? 'Turn off' : 'Turn on';
    case 'Jug': return f.on ? 'Set down' : 'Pour';
    case 'Token': return 'Work';
    case 'Idol': return 'Take';
    case 'Valve': return f.on ? 'Shut' : 'Open';
    case 'Brazier': return f.on ? 'Douse' : 'Light';
    case 'Bell': return 'Ring';
    case 'Portal': return 'Step through';
    case 'Key': return 'Take';
    case 'Socket': return 'Place';
    case 'Relic': return 'Take';
    case 'Stair': return f.down ? 'Go down' : 'Go up';
    case 'Shrine': return 'Pray';
    case 'Wonder': return 'Behold';
    case 'Curio': return curioShort(f);
    default: return 'Work';
  }
}

/** docs/107: what a sleeping lock holds still (its pieces and its bolt). */
const LOCK_PIECES: ReadonlySet<string> = new Set(['Lock', 'Statue', 'Rune', 'Bell', 'Lever', 'Dial', 'Turntable', 'Prop', 'Switch', 'Jug', 'Bearer', 'Idol', 'Token']);

/** Why a mechanism can't be worked, in words. */
function interactWhy(reason: string, f: FixtureView): string {
  if (/Blocked/.test(reason)) return f.kind === 'Block' ? 'it can be neither pushed nor dragged from there — try another side' : 'blocked';
  if (/NotAdjacent/.test(reason)) return 'stand beside it first';
  if (/Done/.test(reason) && f.kind === 'Curio') return 'it has already done its worst';
  if (/Done/.test(reason)) return f.kind === 'Rune' || f.kind === 'Prop' ? 'already lit' : f.kind === 'Bell' ? 'already rung' : f.kind === 'Socket' ? 'already set' : f.kind === 'Relic' ? 'someone already carries it' : f.kind === 'Task' ? 'already done' : f.kind === 'Shrine' ? 'the shrine is spent' : f.kind === 'Wonder' ? 'this hero has already beheld it' : 'already solved';
  if (/Dormant/.test(reason)) return f.kind === 'Portal' ? 'the stone sleeps — something must wake it' : f.kind === 'Brazier' ? 'too wet to light — the water beside it douses it' : f.kind === 'Socket' ? 'bring its key first' : f.kind === 'Bearer' ? 'its hands are empty — bring it something' : f.kind === 'Idol' ? 'your hands are full — one thing at a time' : f.kind === 'Inscription' ? 'too dark to read — light the fire beside it' : LOCK_PIECES.has(f.kind) ? 'the lock sleeps — something must wake it first' : 'nothing happens';
  // docs/130 OB2: one pair of hands a turn at a task
  if (/Busy/.test(reason)) return 'someone is already at it this turn — one pair of hands a turn';
  if (/WrongVerb/.test(reason)) return 'nothing to do with it';
  return reasonText(reason);
}

/** `IllegalReason` (the sim's variant name) in plain words for the cursor chip and announcements. */
export function reasonText(reason: string): string {
  const r = reason.split(':')[0]!.trim();
  return REASON_TEXT[r] ?? reason.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}
const REASON_TEXT: Record<string, string> = {
  OutOfRange: 'out of range', NoLineOfSight: 'no clear line', Unseen: 'too dark or murky to see that far', NoPath: 'no way through',
  DestinationTaken: 'someone stands there', SlotTaken: 'that slot is planned', WrongSlot: 'not in this slot', Resource: 'not enough left',
  Cooldown: 'still recovering', PerPulse: 'once per turn', PerAdventure: 'used up this adventure', TargetDead: 'already down', TargetHidden: 'hidden',
  StaleSnapshot: 'the board just changed — try again', Downed: 'downed', NotAnEnemy: 'not a foe', NotAnAlly: 'not a friend', Requires: 'its condition is not met',
  BadArgs: 'not a valid target', NoItem: 'none of it left in the bag', FoesInSight: 'not with a foe in sight', Floundering: 'floundering in deep water: only one step out, or hold', Committed: 'plan locked — Unlock plan to change it', NoSuchUnit: 'no one there', UnknownAction: 'not an action of this hero', Encounter: 'the fight is not taking plans', Interact: 'cannot be worked from here',
};

export function parseSlot(s: string): Slot { return s === 'B' || s === 'b' ? 'B' : s === '2' ? 2 : 1; }

/** The side `to` lies on from `from` — the sim's `Dir::from_to` (docs/39 FW4): the longer
 *  axis wins, east or west on an exact diagonal. Never a diagonal. */
export function dirFrom(from: Tile, to: Tile): Direction | undefined {
  const dx = to.x - from.x, dy = to.y - from.y;
  if (dx === 0 && dy === 0) return undefined;
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? 'E' : 'W';
  return dy > 0 ? 'S' : 'N';
}
